import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { Lock, Mail, Shield } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { loginAdmin, formatApiError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await loginAdmin(email.trim().toLowerCase(), password);
      toast({ title: 'Welcome, admin', description: 'Logged in successfully' });
      navigate('/admin/dashboard');
    } catch (err) {
      toast({ title: 'Login failed', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-dark via-primary to-primary-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 text-white">
          <div className="bg-white rounded-2xl inline-block px-4 py-3 mb-4 shadow-2xl">
            <BrandLogo variant="compact" className="h-8 w-auto" />
          </div>
          <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
            <Shield className="w-6 h-6" />Admin Console
          </h1>
          <p className="text-white/70 mt-2">BookMyDriver management portal</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>Use your admin credentials</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-5 h-5 text-muted-foreground/70" />
                  <Input
                    id="email"
                    type="email"
                    className="pl-10 h-12"
                    placeholder="admin@bookmydriver.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="admin-email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-5 h-5 text-muted-foreground/70" />
                  <Input
                    id="password"
                    type="password"
                    className="pl-10 h-12"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="admin-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary-dark h-12"
                disabled={submitting}
                data-testid="admin-login-submit"
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-white/60 mt-6">
          <Button variant="link" className="text-white/70" onClick={() => navigate('/')}>← Back to app</Button>
        </p>
      </div>
    </div>
  );
}
